
import { useState, useEffect } from 'react';
import { NavigateFunction } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { trackConversion } from '@/utils/rewardfulUtils';

interface UseRegistrationFormProps {
  navigate: (path: string) => void;
  handleSubscribe: () => void;
  verifiedSessionId: string;
  auth: ReturnType<typeof useAuth>;
}

export const useRegistrationForm = ({
  navigate,
  handleSubscribe,
  verifiedSessionId,
  auth
}: UseRegistrationFormProps) => {
  const { register } = auth;
  
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [acceptPrivacy, setAcceptPrivacy] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasPaid, setHasPaid] = useState(false);
  const [registrationSuccess, setRegistrationSuccess] = useState(false);
  const [paymentError, setPaymentError] = useState('');
  const [showWelcomePopup, setShowWelcomePopup] = useState(false);
  const [validationErrors, setValidationErrors] = useState<{
    password?: string;
    confirmPassword?: string;
  }>({});

  const validatePassword = (password: string): boolean => {
    // Password should be at least 8 characters with numbers and letters
    const hasMinLength = password.length >= 8;
    const hasNumber = /\d/.test(password);
    const hasLetter = /[a-zA-Z]/.test(password);
    
    if (!hasMinLength || !hasNumber || !hasLetter) {
      setValidationErrors(prev => ({
        ...prev,
        password: 'La contraseña debe tener al menos 8 caracteres e incluir letras y números.'
      }));
      return false;
    }
    
    setValidationErrors(prev => ({ ...prev, password: undefined }));
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    console.log("Form submission started");
    
    // Reset errors
    setValidationErrors({});
    
    // Basic form validation
    if (!name || !email || !password || !confirmPassword) {
      toast.error('Por favor completa todos los campos');
      return;
    }
    
    if (password !== confirmPassword) {
      setValidationErrors(prev => ({
        ...prev, 
        confirmPassword: 'Las contraseñas no coinciden'
      }));
      toast.error('Las contraseñas no coinciden');
      return;
    }
    
    // Password strength validation
    if (!validatePassword(password)) {
      toast.error('La contraseña debe ser más segura');
      return;
    }
    
    if (!acceptTerms) {
      toast.error('Debes aceptar los términos y condiciones');
      return;
    }

    if (!acceptPrivacy) {
      toast.error('Debes aceptar la política de privacidad');
      return;
    }

    // If the user hasn't paid and verification hasn't started, redirect to payment
    if (!hasPaid && !verifiedSessionId) {
      handleSubscribe();
      return;
    }
    
    // Now handle the actual registration
    setIsSubmitting(true);
    console.log("Form validated, proceeding with registration");
    
    try {
      // Clear UI and show a loading toast
      const toastId = toast.loading('Creando tu cuenta...');
      console.log("Attempting registration with:", { email, name });
      
      try {
        // Perform the registration
        await register(email, password, name);
        
        // Track successful registration in Rewardful
        trackConversion(email, { status: 'registration_complete' });
        
        // Dismiss the loading toast and show success
        toast.dismiss(toastId);
        toast.success('¡Cuenta creada correctamente!');
        
        // Show registration success page and welcome popup
        setRegistrationSuccess(true);
        setShowWelcomePopup(true);
        
        // Redirect to home page after a short delay
        setTimeout(() => {
          setShowWelcomePopup(false);
          navigate('/');
        }, 5000);
      } catch (error: any) {
        console.error('Registration error:', error);
        toast.dismiss(toastId);
        
        // Handle specific error cases
        if (error.message && error.message.includes('already registered')) {
          toast.error('Este correo electrónico ya está registrado. Por favor, utiliza otro o inicia sesión.');
        } else if (error.message && (error.message.includes('stronger password') || error.message.includes('weak password'))) {
          toast.error('Por favor, elige una contraseña más segura. Mínimo 8 caracteres con letras y números.');
          setValidationErrors(prev => ({
            ...prev,
            password: 'La contraseña es demasiado débil. Usa al menos 8 caracteres con letras y números.'
          }));
        } else {
          toast.error(`Error al crear la cuenta: ${error.message || 'Por favor, inténtalo de nuevo.'}`);
        }
        
        setIsSubmitting(false);
      }
    } catch (error) {
      console.error('Outer registration process error:', error);
      toast.error('Error al crear la cuenta. Por favor, inténtalo de nuevo.');
      setIsSubmitting(false);
    }
  };

  return {
    name,
    setName,
    email,
    setEmail,
    password,
    setPassword,
    confirmPassword,
    setConfirmPassword,
    acceptTerms,
    setAcceptTerms,
    acceptPrivacy,
    setAcceptPrivacy,
    isSubmitting,
    hasPaid,
    setHasPaid,
    registrationSuccess,
    setRegistrationSuccess,
    paymentError,
    setPaymentError,
    showWelcomePopup,
    setShowWelcomePopup,
    validationErrors,
    setValidationErrors,
    handleSubmit,
    validatePassword
  };
};
